import { useCallback, useEffect, useRef, useState } from "react";
import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { UseCardInputReturn } from "./useCardInput";

export type CardFunding = "credit" | "debit" | "prepaid" | "unknown";
export type NotSaveableFunding = Exclude<CardFunding, "credit" | "debit">;

/** Error code the card-save endpoints return for a card whose funding type can't be saved. */
export const ERROR_CODE_CARD_NOT_SAVEABLE = "NON_CREDIT_CARD_NOT_ALLOWED";

// Mirrors oppizi-api's rule (PAD-32618): credit and debit cards can be saved for future payments,
// prepaid and unknown-type cards can only pay once.
export const isSaveableFunding = (funding: CardFunding | string | null | undefined): boolean =>
  funding === "credit" || funding === "debit";

// Shown under the "Save card" checkbox when the entered card can't be saved. Names the card type and
// says what will happen instead, so the client never reads it as "this card can't be used".
export const CARD_NOT_SAVEABLE_HELPERS: Record<NotSaveableFunding, string> = {
  prepaid: "Prepaid cards can't be saved for future payments. This card will be used for this payment only.",
  unknown: "We couldn't verify this card's type, so it can't be saved. This card will be used for this payment only.",
};

export interface UseCardFundingReturn {
  /** Funding of the card currently entered; null until known and again once the number changes. */
  funding: CardFunding | null;
  isCheckingFunding: boolean;
  /**
   * Records a server-side "can't be saved" rejection for the current card (when the Stripe check couldn't run)
   * and returns the funding it recorded.
   */
  markNotSaveable: (funding?: string | null) => NotSaveableFunding;
}

export const toCardFunding = (value: string | null | undefined): CardFunding =>
  value === "credit" || value === "debit" || value === "prepaid" ? value : "unknown";

// Stripe's change events only expose the brand, so the card is tokenized once per card number to read its
// funding. Nothing is saved or charged; the unattached PaymentMethod is simply discarded.
export function useCardFunding({
  cardNumberRevision,
  cardNumberComplete,
  expiryComplete,
  cvcComplete,
}: UseCardInputReturn): UseCardFundingReturn {
  const stripe = useStripe();
  const elements = useElements();
  const [result, setResult] = useState<{ revision: number; funding: CardFunding } | null>(null);
  const [checkingRevision, setCheckingRevision] = useState<number | null>(null);
  const checkedRevisionRef = useRef<number | null>(null);
  const revisionRef = useRef(cardNumberRevision);
  revisionRef.current = cardNumberRevision;

  const allComplete = cardNumberComplete && expiryComplete && cvcComplete;

  useEffect(() => {
    if (!allComplete || !stripe || !elements || checkedRevisionRef.current === cardNumberRevision) return;
    const card = elements.getElement(CardNumberElement);
    if (!card) return;

    const revision = cardNumberRevision;
    checkedRevisionRef.current = revision;
    setCheckingRevision(revision);
    // A failed check is retried on the next completion; the save endpoint's rejection is the backstop.
    const allowRetry = () => {
      if (checkedRevisionRef.current === revision) checkedRevisionRef.current = null;
    };
    stripe
      .createPaymentMethod({ type: "card", card })
      .then(({ paymentMethod }) => {
        if (revisionRef.current !== revision) return; // the number changed while Stripe was answering
        if (paymentMethod?.card) {
          setResult({ revision, funding: toCardFunding(paymentMethod.card.funding) });
        } else {
          allowRetry();
        }
      })
      .catch(allowRetry)
      .finally(() => setCheckingRevision((current) => (current === revision ? null : current)));
  }, [allComplete, stripe, elements, cardNumberRevision]);

  const markNotSaveable = useCallback((funding?: string | null): NotSaveableFunding => {
    const known = toCardFunding(funding);
    // The server refused to save it, so a funding it reports as saveable can only be a classification gap.
    const recorded: NotSaveableFunding = isSaveableFunding(known) ? "unknown" : (known as NotSaveableFunding);
    setResult({ revision: revisionRef.current, funding: recorded });
    return recorded;
  }, []);

  // An edited (incomplete) number is a different card until it completes again.
  const isCurrent = (revision: number | null) => cardNumberComplete && revision === cardNumberRevision;
  return {
    funding: result && isCurrent(result.revision) ? result.funding : null,
    isCheckingFunding: isCurrent(checkingRevision),
    markNotSaveable,
  };
}
